import axios from 'axios';
import store from '../store';
import router from '../router';
import PerimeterxService from './PerimeterxService';

export default {
    install: install,
};


function install(Vue) {
    /**
     * A flag to ensure we don't try to fetch the refresh token twice (this breaks things).
     *
     * @type {boolean}
     */
    let isAlreadyFetchingAccessToken = false;

    /**
     * An array to hold the calls that queued while we were refreshing.
     *
     * @type {*[]}
     */
    let subscribers = [];

    /**
     * Create a separate instance of Axios.
     *
     * @type {AxiosInstance}
     */
    const axiosInstance = axios.create({});

    axiosInstance.interceptors.response.use(response => response, (error) => {
        return responseError(error, this);
    });

    axiosInstance.interceptors.request.use(addAuthorizationHeader);
    Vue.backendApi = _backendApi;

    /**
     * When we get a valid access token, try all our queued calls again.
     *
     * @param accessToken
     */
    function onAccessTokenFetched(accessToken) {
        // When the refresh is successful, we start retrying the requests one by one and empty the queue
        subscribers.forEach((callback) => {
            return callback(accessToken);
        });

        subscribers = [];
    }

    /**
     * Adds a queued caller.
     *
     * @param callback
     */
    function addSubscriber(callback) {
        subscribers.push(callback);
    }

    /**
     * Attempts to get a new token, and replays any captured requests if successful.
     *
     * @see  https://www.techynovice.com/setting-up-JWT-token-refresh-mechanism-with-axios/
     *
     * @param error
     * @returns {Promise<unknown>}
     */
    async function resetTokenAndReattemptRequest(error) {
        try {
            const { response: errorResponse } = error;

            const kaoshiMoney = JSON.parse(localStorage.getItem('kaoshi-money'));
            const { authToken } = kaoshiMoney;

            const payload = { refresh_token: authToken.refresh_token };
            if (!payload) {
                // We can't refresh, throw the error anyway.
                return Promise.reject(error);
            }

            /*
             * Proceed to the token refresh procedure. We create a new Promise that will retry the request, clone all
             * the request configuration from the failed request in the error object.
             */
            const retryOriginalRequest = new Promise((resolve) => {
                /*
                 * We need to add the request retry to the queue since there another request that already attempt to
                 * refresh the token
                 */
                addSubscriber((accessToken) => {
                    // The original request's configuration (including the form data, base URL etc) is in the error
                    // response configuration.
                    const originalRequest = errorResponse.config;
                    originalRequest.headers.Authorization = `Bearer ${accessToken}`;

                    resolve(axios(originalRequest));
                });
            });

            if (!isAlreadyFetchingAccessToken) {
                isAlreadyFetchingAccessToken = true;

                await Vue.backendApi().auth().refreshLogin(payload).then((result) => {
                    if (!result.data) {
                        return Promise.reject(error);
                    }

                    const { data } = result;

                    store.commit('storeAuthToken', data.token);
                    store.commit('storeUser', data.user);

                    store.commit('setCurrentLoginTime');

                    const newToken = data.token.access_token;
                    isAlreadyFetchingAccessToken = false;
                    return onAccessTokenFetched(newToken);
                });
            }

            return retryOriginalRequest;
        } catch (err) {
            return Promise.reject(err);
        }
    }

    /**
     * Handles the "error" responses.
     *
     * Use the refresh token, if available, to login; otherwise this logs the user out.
     *
     * @param error
     * @returns {Promise<void>}
     */
    async function responseError(error) {
        if (error instanceof axios.Cancel) {
            return Promise.reject(error.message);
        }

        const { response } = error;

        if (!response) {
            let { message } = error;
            if (message === 'Network Error') {
                message = 'You may be experiencing connectivity issues. Check your internet connection';
            }
            return Promise.reject(message);
        }

        const { data, status } = response;
        const { message } = data;

        if (data.action && data.action === 'captcha') {
            return PerimeterxService.showCaptcha(data).then(() => {
                console.log('captcha resolved, retry request');
                return axiosInstance.request(error.config);
            });
        }

        if (status === 503 || status === 502) {
            if (window.location.href.indexOf('/maintenance') < 0) {
                window.location.href = '/maintenance';
                return Promise.resolve();
            }

            return Promise.resolve();
        }

        const invalidCredentials = new RegExp('^You entered an invalid username or password$');
        if (message.match(invalidCredentials)) {
            return Promise.reject(message);
        }

        if (message.toLowerCase().indexOf('timeout acquiring a connection') > 0) {
            return Promise.reject(new Error('Service temporarily unavailable, please try again'));
        }

        // TODO: fix redirect to tfa
        // const needsTfa = new RegExp('^Please validate with two-factor-auth before continuing$');
        // if (message.match(needsTfa)) {
        //     window.location = '/tfa-confirm';
        //     return Promise.resolve();
        // }

        const sessionHasExpired = new RegExp('^(Your access token has expired|Expired user credentials)\.?$');
        if (message.match(sessionHasExpired)) {
            try {
                const result = await resetTokenAndReattemptRequest(error);
                return result;
            } catch (e) {
                // @todo Sending these through the Regular Expression engine is a little silly but it works...
                if (e && typeof e === 'string') {
                    const refreshExpired = new RegExp('.*token has expired.*');
                    const refreshIsInvalid = new RegExp('.*token is invalid.*');

                    if (e.match(refreshExpired) || e.match(refreshIsInvalid)) {
                        // @todo Fix 283 properly.
                        store.commit('storeUser', {});
                        localStorage.removeItem('user');

                        store.commit('storeAuthToken', {});
                        localStorage.removeItem('authToken');

                        const { handlingSession } = store.state;
                        store.commit('setHandlingSession', false);

                        // If we are handling the session, they'll already have seen (and possibly read!) a dialog
                        // explaining why they've been logged out.
                        if (!handlingSession) {
                            const alertConfig = {
                                toast: false,
                                showCancelButton: false,
                                showConfirmButton: true,

                                backdrop: false,
                                allowOutsideClick: false,

                                type: 'info',
                                title: 'Session Ended',
                                text: 'You were logged out because your session ended.',
                            };

                            Vue.swal.fire(alertConfig);
                        }

                        if (router.currentRoute.meta && router.currentRoute.meta.forceLogin) {
                            window.location = `/login?redirectTo=${router.currentRoute.fullPath}`;
                        } else if (window.location === '/') {
                            window.location = '/login';
                        } else {
                            window.location = '/';
                        }

                        return Promise.resolve();
                    }

                    console.log(e)
                }

                console.error(`Sending unknown message: ${error.response.data.message}`);
                return Promise.reject(error.response.data.message);
            }
        }

        return Promise.reject(error.response.data.message);
    }

    function addAuthorizationHeader(request) {
        if (store.state.authToken && store.state.authToken.access_token) {
            request.headers.Authorization = `${store.state.authToken.token_type} ${store.state.authToken.access_token}`;
        }

        return request;
    }

    // eslint-disable-next-line no-underscore-dangle
    function _backendApi() {
        return {
            auth,
            banks,
            currencies,
            countries,
            deliveryMethods,
            file,
            login,
            logout,
            notifications,
            order,
            pendingMatches,
            postComment,
            rateAlert,
            thirdPartyOrders,
            thirdPartyCompanies,
            paymentRequest,
            unregisteredPaymentRequest,
            user,
            emailAPI,
            uploadAttachement,
            cardVerification,
            docVerification,
            extensionProblemReports,
            sendEmailMessage,
            inviteUser,
            log,
            paystack,
            mono,
            healthInsuranceProviders,
            healthInsurancePlans,
            healthInsuranceEnrollees,
            healthInsurancePayments,
            referralTransactions,
            worldremit,
            exchangeRateLimits,
            ribbons,
            unified,
        };

        /**
         * Authentication and user confirmation methods.
         *
         * @returns {{passwordResetConfirm: *, passwordReset: *,
         *     userConfirmationLinkSend: *, userConfirmationLinkResend: *}}
         */
        function auth() {
            return {
                passwordReset: passwordReset,
                passwordResetConfirm: passwordResetConfirm,
                refreshLogin: refreshLogin,
                // "Send" and "Resend" are deliberate synonyms!
                userConfirmationLinkSend: userConfirmationLinkSend,
                userConfirmationLinkResend: userConfirmationLinkSend,
                validateInvitationToken,
                validateReferralCode,
            };

            function passwordReset(payload) {
                return axiosInstance.post(`/api/auth/password-reset`, payload);
            }

            function passwordResetConfirm(payload) {
                return axiosInstance.post(`/api/auth/password-reset-confirm`, payload);
            }

            function userConfirmationLinkSend(payload) {
                return new Promise((resolve, reject) => {
                    if (!payload.email || 0 === payload.email.length) {
                        reject('Email is required.');
                    }

                    resolve(axiosInstance.post('/api/auth/user-confirmation-link-resend', payload));
                });
            }

            function validateInvitationToken(payload) {
                return axiosInstance.post('/api/auth/validate-invitation-token', payload);
            }

            function validateReferralCode(payload) {
                return axiosInstance.post('/api/auth/validate-referral-code', payload);
            }
        }

        function paystack () {
            return {
                resolveAccount,
            };

            function resolveAccount(payload) {
                return axiosInstance.post(`/api/paystack/resolve-account`, payload);
            }
        }

        function mono() {
            return {
                reauthoriseAccount,
            };

            function reauthoriseAccount(id) {
                return axiosInstance.post(`/api/mono/reauthorise-account`, {
                    id,
                });
            }

        }

        function login(payload) {
            const actualPayload = { ...{ forceLogout: false, forceLogoutMessage: null }, ...payload };
            return axiosInstance.post('/api/user/login', actualPayload);
        }

        /**
         * Gets a new token, given a valid refresh token.
         *
         * @param payload
         * @returns {AxiosPromise<any>}
         */
        function refreshLogin(payload) {
            return axiosInstance.post('/api/user/refreshLogin', payload);
        }

        function logout(userId) {
            if (userId) {
                let uri = encodeURI(`/api/user/logout?userId=${userId}`);
                return axiosInstance.post(uri);
            }

            return axiosInstance.post('/api/user/logout');
        }

        function currencies() {
            return {
                list: list
            }

            function list() {
                return axiosInstance.get('/api/currencies');
            }
        }

        function countries() {
            return {
                list: list,
            }

            function list(params) {
                return axiosInstance.get('/api/countries', {
                    params,
                });
            }
        }

        function deliveryMethods() {
            return {
                deliveryMethods: getDeliveryMethods,
                deliveryMethodProviders,
                providers,
                providerBranches,
                rules,
            };

            /**
             * Renamed to avoid eslint "shadowing" error.
             *
             * @returns {AxiosPromise<any>}
             */
            function getDeliveryMethods() {
                // return axiosInstance.get('/api/delivery_methods/delivery_method_providers');
                return axiosInstance.get('/api/delivery-methods/delivery-methods');
            }

            function deliveryMethodProviders(params = {}) {
                return axiosInstance.get('/api/delivery-methods/delivery-method-providers', {params});
            }

            function providerBranches() {
                return axiosInstance.get('/api/delivery-methods/provider-branches');
            }

            function providers() {
                return axiosInstance.get('/api/delivery-methods/providers');
            }

            function rules() {
                return axiosInstance.get('/api/delivery-methods/rules');
            }
        }

        function order(orderId) {
            return {
                listOrders: listOrders,
                getOrder: getOrder,
                canCancelOrder: canCancelOrder,
                cancelOrder: cancelOrder,
                listUsersPostedOrders: listUsersPostedOrders,
                listUsersMatchedOrders: listUsersMatchedOrders,
                storeGlobalOffer: storeGlobalOffer,
                matchOffer: matchOffer,
                validateMatchOfferRequest,
                validatePaymentRequest,
                matches: matches,
                globalOffers: globalOffers,
                getPaymentInfo: getPaymentInfo,
                validateTransaction: validateTransaction,
                initiatePayment: initiatePayment,
                completePayment: completePayment,
                listReceivedOrders,
                getMatcherFeeOptions,
                getRandomMatcherFee,
                getPaymentWindow,
                getHybrid,
                updateMemo,
                getPosterFee,
                initiateMonoPayment,
                verifyPosterPayment,
                getMostCompetitiveExchangeRate,
                exportTransfers,
                exportReceivedOrders,
                getRank,
            };

            function listOrders() {
                return axiosInstance.get('/api/order/list');
            }

            function getOrder() {
                return axiosInstance.get(`/api/order/${orderId}`);
            }

            function canCancelOrder() {
                return axiosInstance.get(`/api/order/${orderId}/canCancel`);
            }

            function cancelOrder() {
                return axiosInstance.post(`/api/order/${orderId}/cancel`);
            }

            function listUsersPostedOrders(userId, payload = {}) {
                return axiosInstance.get(`/api/order/user/${userId}`, { params: payload });
            }

            function listUsersMatchedOrders(userId, payload = {}) {
                return axiosInstance.get(`/api/order/user/${userId}/transfers`, { params: payload });
            }

            function storeGlobalOffer(payload) {
                return axiosInstance.post('/api/order/store-global-offer', payload);
            }

            function matchOffer(payload) {
                return axiosInstance.post(`/api/order/${orderId}/match-offer`, payload);
            }

            function validateMatchOfferRequest(payload = {}) {
                return axiosInstance.post(`/api/order/${orderId}/validate-match-request`, payload);
            }

            function validatePaymentRequest(payload = {}) {
                return axiosInstance.post(`/api/order/${orderId}/validate-payment-request`, payload);
            }

            function matches() {
                return {
                    listPendingMatches: listPendingMatches,
                    confirmedMatch: confirmedMatch,
                };

                function listPendingMatches() {
                    return axiosInstance.get(`/api/order/${orderId}/matches/pending-matches`);
                }

                function confirmedMatch(matchId) {
                    return {
                        user: user,
                    };

                    function user(userId) {
                        return {
                            getProofOfPayment: getProofOfPayment,
                            storeProofOfPayment: storeProofOfPayment,
                            recipient: recipient
                        };

                        function getProofOfPayment() {
                            return axiosInstance.get(`/api/pending-matches/${matchId}/user/${userId}/payment`);
                        }

                        function storeProofOfPayment(payload) {
                            return axiosInstance.post(`/api/pending-matches/${matchId}/user/${userId}/payment`, payload)
                        }

                    }
                }

            }

            function globalOffers(params) {
                return axiosInstance.get('/api/order/global-offers', {
                    params,
                });
            }

            function getPaymentInfo() {
                return axiosInstance.get(`/api/order/${orderId}/payment-info`);
            }

            function validateTransaction(matchId, payload = {}) {
                return new Promise((resolve, reject) => {
                    const interval = setTimeout(() => {
                        reject('Oops! Bank Connection Is Down!');
                    }, 1.5 * 60 * 1000);

                    axiosInstance.post(`/api/order/validate-transaction/${matchId}`, payload)
                        .then(resolve)
                        .catch(reject);
                });
            }

            function initiatePayment() {
                return axiosInstance.post(`/api/order/${orderId}/initiate-payment`);
            }

            function completePayment(type, payload) {
                return axiosInstance.post(`/api/order/${orderId}/complete-payment/${type}`, payload);
            }

            function listReceivedOrders(userId, payload = {}) {
                return axiosInstance.get(`/api/user/${userId}/received-orders`, { params: payload });
            }

            function getMatcherFeeOptions(params) {
                return axiosInstance.get(`/api/order/${orderId}/matcher-fee-options`, {
                  params,
                });
            }

            function getRandomMatcherFee(params) {
                return axiosInstance.get(`/api/order/${orderId}/random-fee`, {
                  params,
                });
            }

            function getPaymentWindow() {
                return axiosInstance.get('/api/order/payment-window');
            }

            function getHybrid(params) {
                return axiosInstance.get('/api/order/hybrid', {
                    params,
                });
            }

            function updateMemo(payload) {
                return axiosInstance.post(`/api/order/${orderId}/memo`, payload);
            }

            function getPosterFee(payload) {
                return axiosInstance.get(`/api/order/poster-fee`, { params: payload });
            }

            function initiateMonoPayment(payload) {
                return axiosInstance.post(`/api/order/${orderId}/initiate-mono-payment`, payload);
            }

            function verifyPosterPayment(payload) {
                return axiosInstance.post(`/api/order/${orderId}/verify-poster-payment`, payload);
            }

            function getMostCompetitiveExchangeRate(params) {
                return axiosInstance.get(`/api/order/most-competitive-exchange-rate`, { params });
            }

            function exportTransfers(userId, payload = {}) {
                return axiosInstance.post(`/api/user/${userId}/export-transfers`, payload);
            }

            function exportReceivedOrders(userId, payload = {}) {
                return axiosInstance.post(`/api/user/${userId}/export-received-orders`, payload);
            }

            function getRank(params) {
                return axiosInstance.get('/api/order/rank', { params });
            }
        }

        function pendingMatches(matchId) {
            return {
                getPendingMatches: getPendingMatches,
                acceptPendingMatch: acceptPendingMatch,
                confirmPendingMatch: confirmPendingMatch,
                rejectPendingMatch: rejectPendingMatch,
                updateMemo,
                getCount,
                getCancelledCountInLast30Days,
                worldremit,
            }

            function getPendingMatches() {
                return axiosInstance.get(`/api/pending_matches/${matchId}`);
            }

            function acceptPendingMatch() {
                return axiosInstance.post(`/api/pending-matches/${matchId}/accept`);
            }

            function confirmPendingMatch() {
                return axiosInstance.post(`/api/pending-matches/${matchId}/confirm`);
            }

            function rejectPendingMatch() {
                return axiosInstance.post(`/api/pending-matches/${matchId}/reject`);
            }

            function updateMemo(payload) {
                return axiosInstance.post(`/api/pending-matches/${matchId}/memo`, payload);
            }

            function getCount(status = 'active') {
                return axiosInstance.get(`/api/pending-matches/count/${status}`);
            }

            function getCancelledCountInLast30Days() {
                return axiosInstance.get('/api/pending-matches/count-cancelled-in-30-days');
            }

            function worldremit() {
                return {
                    getQuote(payload) {
                        return axiosInstance.post(`/api/pending-matches/${matchId}/worldremit/get-quote`, payload);
                    },
                    pay(payload) {
                        return axiosInstance.post(`/api/pending-matches/${matchId}/worldremit/pay`, payload);
                    },
                };
            }
        }

        function banks() {
            return {
                nigerian: nigerian,
                ghanian: ghanian
            };

            function nigerian() {
                return {
                    listNigerianBanks: listNigerianBanks
                };

                function listNigerianBanks() {
                    return axiosInstance.get('/api/banks/nigerian/list');
                }
            }

            function ghanian() {
                return {
                    listGhanianBanks: listGhanianBanks
                };

                function listGhanianBanks() {
                    return axiosInstance.get('/api/banks/ghanian/list');
                }
            }
        }

        function user(userId) {
            return {
                fetchKaoshiUser: fetchKaoshiUser,
                getUser: getUser,
                getUserRating: getUserRating,
                storeUser: storeUser,
                changePassword: changePassword,
                getUserSettings: getUserSettings,
                getUserComments: getUserComments,
                ping: ping,
                storeNotificationSettings: storeNotificationSettings,
                storeKaoshiTestPaymentSettings,
                storeReferralSettings,
                storePrivacySettings: storePrivacySettings,
                recipient: recipient,
                getRateAlerts: getRateAlerts,
                getNotifications: getNotifications,
                getActiveTransactions: getActiveTransactions,
                storeNotificationActivity: storeNotificationActivity,
                getFormFillerData: getFormFillerData,
                getFormFillerToPayRecipient: getFormFillerToPayRecipient,
                getPublicKey: getPublicKey,
                storeFormFillerData: storeFormFillerData,
                formFillerGeneratePassword,
                formFillerFetchPassword,
                formFillerFetchCredentials,
                formFillerSaveCredentials,
                formFillerUpdate3poList,
                formFillerGetBrands,
                formFillerUserLogout,
                getIdentityVerificationUrl,
                getIdentityVerificationData,
                getAddressVerificationUrl,
                getDeliveryMethods,
                getSettingsDeliveryMethod,
                createDeliveryMethod,
                updateDeliveryMethod,
                deleteDeliveryMethod,
                authenticateDeliveryMethod,
                authenticatePaymentMethod,
                createKlarnaAccountDetailsFlow: createKlarnaAccountDetailsFlow,
                createPlaidAccountDetailsLinkToken,
                getPaymentMethods,
                createPaymentMethod,
                updatePaymentMethod,
                deletePaymentMethod,
                storeTwoFactorAuth,
                getOptions,
                storeOptions,
                tfaConfirm,
                resendTfaCode,
                getPaymentMethodCardVerificationUrl,
                getAddresses,
                confirmPhoneNumber,
                getPostersBeneficiaries,
                addToWaitlist,
                getInvitedUsers,
                hasAccountWith,
            };

            /**
             * Gets the current public key.
             *
             * @returns {AxiosPromise<any>}
             */
            function getPublicKey() {
                return axios.get('/api/user/form-filler/get-public-key');
            }

            function getFormFillerToPayRecipient(orderId, type, params = {}) {
                return axiosInstance.get(`/api/user/form-filler/get-form-filler-data-to-pay-recipient/${orderId}/${type}`, {
                    params,
                });
            }

            function fetchKaoshiUser(kaoshiHandle) {
                return axiosInstance.get(`/api/user/fetch-kaoshi-info/${kaoshiHandle}`);
            }

            /**
             * Get the form filler data.
             *
             * @returns {AxiosPromise<any>}
             */
            function getFormFillerData() {
                const uri = '/api/user/form-filler';
                return axiosInstance.get(uri);
            }

            function getUser() {
                return axiosInstance.get(`/api/user/${userId}`);
            }

            function getUserRating() {
                return axiosInstance.get(`/api/user/${userId}/rating/`);
            }

            function ping() {
                return axiosInstance.get(`/api/ping/${userId}`);
            }

            function storeFormFillerData(payload) {
                return axiosInstance.post('/api/user/form-filler', payload);
            }

            function formFillerGeneratePassword(payload) {
                return axiosInstance.post('/api/user/form-filler/generate-password', payload);
            }

            function formFillerFetchPassword(payload) {
                return axiosInstance.post('/api/user/form-filler/fetch-password', payload);
            }

            function formFillerFetchCredentials(payload) {
                return axiosInstance.post('/api/user/form-filler/fetch-credentials', payload);
            }

            function formFillerSaveCredentials(payload) {
                return axiosInstance.post('/api/user/form-filler/save-credentials', payload);
            }

            function formFillerUpdate3poList(payload = {}) {
                return axiosInstance.post('/api/user/form-filler/update-3po-list', payload);
            }

            function formFillerGetBrands(payload = {}) {
                return axiosInstance.post('/api/user/form-filler/brands', payload);
            }

            function formFillerUserLogout(payload = {}) {
                return axiosInstance.post('/api/user/form-filler/logout', payload);
            }

            function storeUser(user, headers) {
                return axiosInstance.post(`/api/user/${userId}/store-user`, user, {
                    headers,
                });
            }

            function changePassword(user) {
                return axiosInstance.post(`/api/user/${userId}/change-password`, user);
            }

            function getUserSettings() {
                return axiosInstance.get(`/api/user/${userId}/user-settings`);
            }

            function getUserComments() {
                return axiosInstance.get(`/api/user/${userId}/user-comments`, userId);
            }

            function storeNotificationSettings(payload) {
                const { CancelToken } = axios;
                const source = CancelToken.source();
                const promise = axiosInstance.post(`/api/user/${userId}/notification-settings`, payload,
                    { cancelToken: source.token });
                promise.pendingRequest = source;
                return promise;
            }

            function storePrivacySettings(payload) {
                return axiosInstance.post(`/api/user/${userId}/privacy-settings`, payload);
            }

            function recipient(recipientId) {
                return {
                    listRecipients: listRecipients,
                    storeRecipient: storeRecipient,
                    deleteRecipient: deleteRecipient,
                };

                function listRecipients(params) {
                    return axiosInstance.get(`/api/user/${userId}/recipient/list`, {
                        params,
                    });
                }

                function storeRecipient(recipient) {
                    if (recipientId) {
                        return axiosInstance.post(`/api/user/${userId}/recipient/${recipientId}`, recipient);
                    } else {
                        return axiosInstance.post(`/api/user/${userId}/recipient`, recipient);
                    }
                }

                function deleteRecipient() {
                    return axiosInstance.delete(`/api/user/${userId}/recipient/${recipientId}`)
                }
            }

            function getRateAlerts() {
                return axiosInstance.get(`/api/user/${userId}/rate-alert`);
            }

            function getNotifications() {
                return axiosInstance.get(`/api/user/${userId}/notifications`);
            }

            function getActiveTransactions() {
                return axiosInstance.get(`/api/user/${userId}/active-transactions`);
            }

            function storeNotificationActivity(payload) {
                return axiosInstance.put(`/api/user/${userId}/notification-activity`, payload);
            }

            function storeKaoshiTestPaymentSettings(payload) {
                return axiosInstance.put(`/api/user/${userId}/test-payment-settings`, payload);
            }

            function storeReferralSettings(payload) {
                return axiosInstance.put(`/api/user/${userId}/referral-settings`, payload);
            }

            function getIdentityVerificationUrl() {
                return axiosInstance.get(`/api/user/${userId}/identity-verification`);
            }

            function getIdentityVerificationData() {
                return axiosInstance.get(`/api/user/${userId}/identity-verification-data`);
            }

            function getAddressVerificationUrl() {
                return axiosInstance.get(`/api/user/${userId}/address-verification`);
            }

            function getDeliveryMethods() {
                return axiosInstance.get(`/api/user/${userId}/delivery-method`);
            }

            function getSettingsDeliveryMethod() {
                return axiosInstance.get(`/api/user/${userId}/settings/delivery-method`);
            }

            function createDeliveryMethod(payload) {
                return axiosInstance.post(`/api/user/${userId}/delivery-method`, payload);
            }

            function updateDeliveryMethod(id, payload) {
                return axiosInstance.put(`/api/user/${userId}/delivery-method/${id}`, payload);
            }

            function deleteDeliveryMethod(id) {
                return axiosInstance.delete(`/api/user/${userId}/delivery-method/${id}`);
            }

            function authenticateDeliveryMethod(id) {
                return axiosInstance.post(`/api/user/${userId}/delivery-method/${id}/authenticate`);
            }

            function authenticatePaymentMethod(id) {
                return axiosInstance.post(`/api/user/${userId}/payment-method/${id}/authenticate`);
            }

            function createKlarnaAccountDetailsFlow() {
                return axiosInstance.post(`/api/user/${userId}/klarna/account-details/create-flow`);
            }

            function createPlaidAccountDetailsLinkToken(accessToken = null, redirectUri = null) {
                return axiosInstance.post(`/api/user/${userId}/plaid/account-details/link-token`, {
                    accessToken,
                    redirect_uri: redirectUri,
                });
            }

            function getPaymentMethods() {
                return axiosInstance.get(`/api/user/${userId}/payment-method`);
            }

            function createPaymentMethod(payload) {
                return axiosInstance.post(`/api/user/${userId}/payment-method`, payload);
            }

            function updatePaymentMethod(id, payload) {
                return axiosInstance.put(`/api/user/${userId}/payment-method/${id}`, payload);
            }

            function deletePaymentMethod(id) {
                return axiosInstance.delete(`/api/user/${userId}/payment-method/${id}`);
            }

            function storeTwoFactorAuth(payload) {
                return axiosInstance.put(`/api/user/${userId}/two-factor-auth`, payload);
            }

            function getOptions(params) {
                return axiosInstance.get(`/api/user/${userId}/options`, {
                    params,
                });
            }

            function storeOptions(payload) {
                return axiosInstance.post(`/api/user/${userId}/options`, payload);
            }

            function tfaConfirm(payload, authorizationHeader) {
                return axios.post(`/api/user/${userId}/tfa-confirm`, payload, {
                  headers: { Authorization: authorizationHeader }
                }).then((response) => response).catch(responseError);
            }

            function resendTfaCode(payload, authorizationHeader) {
                return axios.post(`/api/user/${userId}/resend-tfa-code`, payload, {
                  headers: { Authorization:  authorizationHeader }
                }).then((response) => response).catch(responseError);
            }

            function getPaymentMethodCardVerificationUrl() {
                return axiosInstance.get(`/api/user/payment-method/card-verification`);
            }

            function getAddresses() {
                return axiosInstance.get(`/api/user/${userId}/addresses`);
            }

            function confirmPhoneNumber(phone) {
                return axiosInstance.post(`/api/user/${userId}/confirm-phone-number`, {
                    phone,
                });
            }

            function getPostersBeneficiaries(params) {
                return axiosInstance.get(`/api/user/${userId}/posters-beneficiaries`, {
                    params,
                });
            }

            function addToWaitlist(payload) {
                return axiosInstance.post('/api/user/waitlist', payload);
            }

            function getInvitedUsers(params) {
                return axiosInstance.get(`/api/user/${userId}/invited-users`, {
                    params,
                });
            }

            function hasAccountWith(company) {
                return axiosInstance.get(`/api/user/has-account-with/${company}`, {});
            }
        }

        function rateAlert(data) {
            return {
                deleteRateAlert,
                storeRateAlert,
                updateRateAlert,
            };

            function deleteRateAlert(payload) {
                const rateAlertId = payload;
                return axiosInstance.delete(`/api/rate-alert/delete-rate-alert/${rateAlertId}`);
            }

            function storeRateAlert(payload) {
                return axiosInstance.post('/api/rate-alert/store-rate-alert', payload);
            }

            function updateRateAlert(payload) {
                const rateAlertId = payload.id;
                return axiosInstance.put(`/api/rate-alert/update-rate-alert/${rateAlertId}`, payload);
            }
        }

        /**
         *
         */
        function paymentRequest() {
            return {
                storePaymentRequest,
                getAll,
                accept,
                decline,
                deletePaymentRequest,
                getCount,
            };

            /**
             * Store the payment request.
             *
             * @param payload
             * @returns {AxiosPromise<any>}
             */
            function storePaymentRequest(payload) {
                return axiosInstance.post('/api/payment-requests', payload);
            }

            function getAll(userId, payload = {}) {
                return axiosInstance.get(`/api/payment-requests/user/${userId}`, { params: payload });
            }

            function accept(requestId, userId, payload = {}) {
                return axiosInstance.post(`/api/payment-requests/${requestId}/accept`, {
                    ...payload,
                    benefactor_id: userId,
                });
            }

            function decline(requestId, userId) {
                return axiosInstance.post(`/api/payment-requests/${requestId}/decline`, { benefactor_id: userId });
            }

            function deletePaymentRequest(requestId) {
                return axiosInstance.post(`/api/payment-requests/${requestId}/delete`, {});
            }

            function getCount(status = 'active') {
                return axiosInstance.get(`/api/payment-requests/count/${status}`);
            }
        }

        function unregisteredPaymentRequest(id, hash) {
            return {
                initiate,
                addBank,
                getMatcherFee,
                accept,
                fetch,
                initiatePayment,
                completePayment,
                validateTransaction,
            };

            function initiate() {
                return axiosInstance.post(`/api/upr/${id}/initiate/${hash}`);
            }

            function addBank(payload) {
                return axiosInstance.post(`/api/upr/${id}/add-bank/${hash}`, payload);
            }

            function getMatcherFee(payload) {
                return axiosInstance.post(`/api/upr/${id}/get-matcher-fee/${hash}`, payload);
            }

            function accept(payload) {
                return axiosInstance.post(`/api/upr/${id}/accept/${hash}`, payload);
            }

            function fetch(payload) {
                return axiosInstance.post(`/api/upr/${id}/fetch/${hash}`, payload);
            }

            function initiatePayment(payload) {
                return axiosInstance.post(`/api/upr/${id}/initiate-payment/${hash}`, payload);
            }

            function completePayment(type, payload) {
                return axiosInstance.post(`/api/upr/${id}/complete-payment/${hash}/${type}`, payload);
            }

            function validateTransaction(matchId, payload) {
                return axiosInstance.post(`/api/upr/${id}/validate-transaction/${hash}/${matchId}`, payload);
            }
        }
        /**
         * The third party order/offer routes.
         *
         * @returns {{storeOrder: (function(*=): AxiosPromise<any>)}}
         */
        function thirdPartyOrders() {
            return {
                listOrders,
                storeOrder,
                fetchPage,
            };

            /**
             * List Third party offers
             *
             * @param params
             * @returns {AxiosPromise<any>}
             */
            function listOrders(params) {
                return axiosInstance.get('/api/third-party-orders', {
                    params,
                })
            }

            /**
             * Store the order.
             *
             * @param payload
             * @returns {AxiosPromise<any>}
             */
            function storeOrder(payload) {
                console.log('L721 Store Order', payload);
                return axiosInstance.post('/api/third-party-orders/store-third-party-order', payload);
            }

            function fetchPage(params) {
                return axiosInstance.get('/api/list-third-party-orders', {
                    params,
                })
            }
        }

        /**
         * The third party order companies.
         */
        function thirdPartyCompanies() {
            return {
                getAll,
                getUsersThirdPartyCompanies,
            };

            /**
             * List Third party order companies
             *
             * @returns {AxiosPromise<any>}
             */
            function getAll() {
                return axiosInstance.get('/api/third-party-companies');
            }

            /**
             * List User's Third party order companies
             *
             * @param params
             * @returns {AxiosPromise<any>}
             */
            function getUsersThirdPartyCompanies(params) {
                const { id: userId } = store.state.user;
                return axiosInstance.get(`/api/user/${userId}/third-party-companies`, {
                    params,
                });
            }
        }

        function file() {
            return {
                storeFile: storeFile
            };

            function storeFile(file) {
                const formData = new FormData();
                formData.append('upload', file);
                return axiosInstance.post('/api/file/store-file', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                });
            }
        }

        function postComment(matchId, payload) {
            return axiosInstance.post(`/api/confirmed-matches/${matchId}/comments`, payload);
        }

        function notifications() {
            return {
                markAsRead,
                destroy,
                action,
            };

            function markAsRead(payload) {
                return axiosInstance.put('/api/notifications/mark-as-read', payload);
            }

            function destroy(id) {
                return axiosInstance.delete(`/api/notifications/${id}`);
            }

            function action(id, payload) {
                return axiosInstance.post(`/api/notifications/${id}/action`, payload);
            }
        }
        /**
         * handle all requests to email service API
         *
         * @param url Url we want to call on email service API
         * @param method Method we want to perfrom (POST/GET)
         * @param data Data we want to send as parameters
         * @returns {AxiosPromise<any>}
         */
        function emailAPI({ url, method, data }) {
            return axiosInstance.post('/api/email-api', {
                url,
                method,
                data,
            });
        }

        /**
         * Send email
         *
         * @param to_emails Recipient email address
         * @param subject Email subject
         * @param body Email message
         * @param parent_thread_id Id of message we want to fetch thread
         * @returns {AxiosPromise<any>}
         */
        function sendEmailMessage({
            to_emails,
            subject,
            body,
            parent_thread_id
        }) {
            return axiosInstance.post('/api/send-email', {
                to_emails,
                subject,
                body,
                parent_thread_id,
            });
        }

        /**
         * Upload attachement
         *
         * @param file Attachement file
         * @returns {AxiosPromise<any>}
         */
        function uploadAttachement(file) {
            const formData = new FormData();
            formData.append('upload', file);
            return axiosInstance.post('/api/upload-email-attachement', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
        }

        function cardVerification(id) {
            return {
                create,
            };

            function create(fileInstance, payload) {
                const formData = new FormData();
                formData.append('file', fileInstance);
                Object.entries(payload).forEach((item) => {
                    const [key, value] = item;
                    formData.append(key, value);
                });

                return axiosInstance.post('/api/card-verification', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
            }
        }

        function docVerification() {
            return {
                create,
            };

            function create(payload) {
                const formData = new FormData();

                Object.entries(payload).forEach((item) => {
                    const [key, value] = item;
                    formData.append(key, value);
                });

                return axiosInstance.post('/api/doc-verification', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
            }
        }

        function healthInsuranceProviders() {
            return {
                getAll,
            };

            function getAll(params) {
                return axiosInstance.get('/api/health-insurance-providers', {
                    params,
                });
            }
        }

        function healthInsurancePlans() {
            return {
                getAll,
            };

            function getAll(params) {
                return axiosInstance.get('/api/health-insurance-plans', {
                    params,
                });
            }
        }

        function healthInsuranceEnrollees() {
            return {
                getAll,
                get,
                create,
            };

            function getAll(params) {
                return axiosInstance.get('/api/health-insurance-enrollees', {
                    params,
                });
            }

            function get(id) {
                return axiosInstance.get(`/api/health-insurance-enrollees/${id}`);
            }

            function create(payload) {
                return axiosInstance.post('/api/health-insurance-enrollees', payload);
            }
        }

        function healthInsurancePayments() {
            return {
                create,
                update,
            };

            function create(payload) {
                return axiosInstance.post('/api/health-insurance-payments', payload);
            }

            function update(payload) {
                return axiosInstance.put(`/api/health-insurance-payments/${payload.id}`, payload);
            }
        }

        function referralTransactions() {
            return {
                getAll,
                getTotalAmount,
                withdraw,
            };

            function getAll(params) {
                return axiosInstance.get('/api/referral-transactions', {
                    params,
                });
            }

            function getTotalAmount() {
                return axiosInstance.get('/api/referral-transactions/amount');
            }

            function withdraw(payload) {
                return axiosInstance.post('/api/referral-transactions/withdraw', payload);
            }
        }

        function exchangeRateLimits() {
            return {
                getAll,
            };

            function getAll(params) {
                return axiosInstance.get('/api/exchange-rate-limits', {
                    params,
                });
            }
        }

        function extensionProblemReports() {
            return {
                create,
            };

            function create(payload) {
                return axiosInstance.post('/api/extension-problem-reports', payload);
            }
        }

        function inviteUser(payload) {
            return axiosInstance.post('/api/user/invite-user', payload);
        }

        function log(payload) {
            return axiosInstance.post('/api/log', payload);
        }

        function worldremit() {
            return {
                register,
                login,
                getActivation,
                activate,
                requestVerification,
                verify,
                getAccount,
                updateEmail,
                sendOtp,
                verifyOtp,
                getToken,
            }

            function register(payload) {
                return axiosInstance.post('/api/worldremit/register', payload);
            }

            function login(payload) {
                return axiosInstance.post('/api/worldremit/login', payload);
            }

            function getActivation(payload) {
                return axiosInstance.get('/api/worldremit/activation');
            }

            function activate(payload) {
                return axiosInstance.post('/api/worldremit/activate', payload);
            }

            function requestVerification(payload) {
                return axiosInstance.post('/api/worldremit/request-verification', payload);
            }

            function verify(payload) {
                return axiosInstance.post('/api/worldremit/verify', payload);
            }

            function getAccount(payload) {
                return axiosInstance.post('/api/worldremit/get-account', payload);
            }

            function updateEmail(payload) {
                return axiosInstance.post('/api/worldremit/update-email', payload);
            }

            function sendOtp(payload) {
                return axiosInstance.post('/api/worldremit/send-otp', payload);
            }

            function verifyOtp(payload) {
                return axiosInstance.post('/api/worldremit/verify-otp', payload);
            }

            function getToken() {
                return axiosInstance.get('/api/worldremit/token');
            }
        }

        function ribbons() {
            return {
                getActive: () => axiosInstance.get('/api/ribbons/active'),
            };
        }

        function unified() {
            return {
                getPaymentPayload: (matchId) => axiosInstance.get(`/api/unified/payment-payload/${matchId}`),
                getLatestTransaction: (matchId) => axiosInstance.get(`/api/unified/latest-transaction/${matchId}`),
            }
        }
    }
}
